import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { Utils, fetchWithAuth } from '../../utils';

/** Change automation status (live / paused / ended / archived). Returns API result, or null. */
export async function changeAutomationStatusAction(appId, automationId, status) {
    try {
        const response = await fetchWithAuth(Utils.baseurl + 'automations/status', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ app_id: appId, id: automationId, status }),
        });
        const result = await response.json();
        if (result.status) {
            toast.success(result.message);
            return result;
        }
        toast.error(result.errors ? result.errors.join(', ') : (result.message || 'Could not update automation status'));
        return null;
    } catch (error) {
        console.error('Error updating automation status:', error);
        toast.error('Could not update automation status');
        return null;
    }
}

/** Duplicate an automation (confirms first). Returns the new automation, or null if cancelled/failed. */
export async function duplicateAutomationAction(appId, automation) {
    if (!automation) return null;
    const confirm = await Swal.fire({
        title: "Duplicate this automation?",
        text: `A new draft copy of "${automation.name}" will be created.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#4c5fd5",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Yes, duplicate",
    });
    if (!confirm.isConfirmed) return null;

    try {
        const response = await fetchWithAuth(Utils.baseurl + 'automations/duplicate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ app_id: appId, id: automation.id }),
        });
        const result = await response.json();
        if (result.status) {
            toast.success('Automation duplicated');
            return result.data;
        }
        toast.error(result.message || 'Could not duplicate automation');
        return null;
    } catch (error) {
        console.error('Error duplicating automation:', error);
        toast.error('Could not duplicate automation');
        return null;
    }
}

/** Delete an automation (confirms first). Returns true on success. */
export async function deleteAutomationAction(appId, automation) {
    if (!automation) return false;
    const isLive = Number(automation.status) === 3;
    const confirm = await Swal.fire({
        title: "Are you sure?",
        text: isLive
            ? `"${automation.name}" is live. Deleting it stops it right away and removes its users' progress and history. This cannot be undone.`
            : `Are you sure you want to delete "${automation.name}"? Its users' progress and history will be removed too. This cannot be undone.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Yes, delete",
    });
    if (!confirm.isConfirmed) return false;

    try {
        const response = await fetchWithAuth(Utils.baseurl + 'automations/delete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ app_id: appId, id: automation.id }),
        });
        const result = await response.json();
        if (result.status) {
            toast.success('Automation deleted');
            return true;
        }
        toast.error(result.message || 'Could not delete automation');
        return false;
    } catch (error) {
        console.error('Error deleting automation:', error);
        toast.error('Could not delete automation');
        return false;
    }
}

/** Fetch audit logs for an automation. */
export async function fetchAutomationAuditLogs(appId, automationId, { page = 1, pageSize = 50 } = {}) {
    try {
        const response = await fetchWithAuth(Utils.baseurl + 'automations/audit-logs', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ app_id: appId, id: automationId, page, pageSize }),
        });
        const result = await response.json();
        if (result.status) return result.data;
        toast.error(result.message || 'Could not load audit logs');
        return null;
    } catch (error) {
        console.error('Error fetching audit logs:', error);
        toast.error('Could not load audit logs');
        return null;
    }
}

/** Fetch live per-node enrollment counts for an automation: { [nodeId]: count }. */
export async function fetchAutomationNodeCounts(appId, automationId) {
    try {
        const response = await fetchWithAuth(Utils.baseurl + 'automations/node-counts', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ app_id: appId, id: automationId }),
        });
        const result = await response.json();
        if (result.status) return result.data || {};
        return {};
    } catch (error) {
        console.error('Error fetching automation node counts:', error);
        return {};
    }
}

/** Fetch the users currently sitting at one node of an automation (paginated). */
export async function fetchAutomationNodeEnrollments(appId, automationId, nodeId, { page = 1, pageSize = 25 } = {}) {
    try {
        const response = await fetchWithAuth(Utils.baseurl + 'automations/node-enrollments', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ app_id: appId, id: automationId, nodeId, page, pageSize }),
        });
        const result = await response.json();
        if (result.status) return result;
        toast.error(result.message || 'Could not load users for this step');
        return null;
    } catch (error) {
        console.error('Error fetching automation node enrollments:', error);
        toast.error('Could not load users for this step');
        return null;
    }
}

/** Estimated WhatsApp reach (app_users with a phone on file) for the given segment selection. */
export async function fetchAutomationSegmentChannelReach(appId, includeSegmentIds, excludeSegmentIds) {
    try {
        const response = await fetchWithAuth(Utils.baseurl + 'automations/segment-channel-reach', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ app_id: appId, include_segment_ids: includeSegmentIds, exclude_segment_ids: excludeSegmentIds }),
        });
        const result = await response.json();
        if (result.status) return result.data || {};
        return {};
    } catch (error) {
        console.error('Error fetching automation segment channel reach:', error);
        return {};
    }
}

/** Create a new blank automation draft. Returns the created automation (with id), or null. */
export async function createAutomationAction(appId, name) {
    try {
        const response = await fetchWithAuth(Utils.baseurl + 'automations/create', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ app_id: appId, name }),
        });
        const result = await response.json();
        if (result.status) return result.data;
        toast.error(result.message || 'Could not create automation');
        return null;
    } catch (error) {
        console.error('Error creating automation:', error);
        toast.error('Could not create automation');
        return null;
    }
}
/**
 * Set an automation live (or resume a paused one) from outside the builder. The server runs the same
 * pre-launch checks as the builder's Set Live modal; if any fail we list them and offer to open
 * the automation so they can be fixed. Returns the API result on success, otherwise null.
 */
export async function setAutomationLiveAction(appId, automation, onOpenAutomation) {
    try {
        const response = await fetchWithAuth(Utils.baseurl + 'automations/status', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ app_id: appId, id: automation.id, status: 3 }),
        });
        const result = await response.json();
        if (result.status) {
            toast.success(result.message);
            return result;
        }
        const failed = Array.isArray(result.checks) ? result.checks.filter((c) => !c.ok) : [];
        if (failed.length || Array.isArray(result.errors)) {
            const lines = (failed.length ? failed.map((c) => c.detail || c.label) : result.errors)
                .map((t) => `<li style="margin-bottom:4px">${String(t).replace(/</g, '&lt;')}</li>`)
                .join('');
            const ask = await Swal.fire({
                title: "Can't set live yet",
                html: `<div style="text-align:left">Fix these first:<ul style="margin:8px 0 0 18px;padding:0">${lines}</ul></div>`,
                icon: "error",
                showCancelButton: Boolean(onOpenAutomation),
                reverseButtons: true,
                confirmButtonColor: "#4c5fd5",
                cancelButtonColor: "#6c757d",
                confirmButtonText: onOpenAutomation ? "Open automation" : "OK",
                cancelButtonText: "Close",
            });
            if (ask.isConfirmed && onOpenAutomation) onOpenAutomation(automation);
            return null;
        }
        toast.error(result.message || 'Could not set automation live');
        return null;
    } catch (error) {
        console.error('Error setting automation live:', error);
        toast.error('Could not set automation live');
        return null;
    }
}
